import React, { useMemo, useState } from "react";

import SignUpView from "../ui/SignUpView";
import { createUser } from "../../../services/userService";
import { emailRegex, passwordRegex } from "../../../constants/common";
import { SignUpScreenProps } from "../auth.types";
import { logAnalyticsEvent } from "../../../firebase";

export default function SignUpScreen({ onSwitchToSignIn }: SignUpScreenProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEmailValid = useMemo(() => emailRegex.test(email.trim()), [email]);
  const isPasswordValid = useMemo(
    () => passwordRegex.test(password),
    [password],
  );

  // keep behavior identical: only blocks while saving
  const canSignUp = !isSaving;

  const onNameChange = (text: string) => {
    setName(text);
    if (error) setError(null);
  };

  const onEmailChange = (text: string) => {
    setEmail(text);
    if (error) setError(null);
  };

  const onPasswordChange = (text: string) => {
    setPassword(text);
    if (error) setError(null);
  };

  const onConfirmPasswordChange = (text: string) => {
    setConfirmPassword(text);
    if (error) setError(null);
  };

  const handleJoin = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      setError("Please enter your name.");
      return;
    }

    if (!trimmedEmail) {
      setError("Please enter your email.");
      return;
    }

    if (!password) {
      setError("Please enter a password.");
      return;
    }

    if (!isEmailValid) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!isPasswordValid) {
      setError(
        "Password must contain at least 1 lowercase, 1 uppercase, 1 digit, and 1 special character.",
      );
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match. Please try again.");
      return;
    }

    setError(null);
    setIsSaving(true);

    try {
      await createUser(trimmedName, trimmedEmail, password);
      // Log successful signup
      logAnalyticsEvent("user_sign_up", {
        timestamp: new Date().toISOString(),
      });
    } catch (err: any) {
      console.error("Error during sign up:", err);

      if (err.code === "auth/email-already-in-use") {
        setError(
          "This email is already registered. Please use a different email or sign in.",
        );
      } else if (err.code === "auth/weak-password") {
        setError("Password is too weak. Please choose a stronger password.");
      } else if (err.code === "auth/invalid-email") {
        setError("Invalid email address format.");
      } else if (err.code === "auth/network-request-failed") {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Failed to create account. Please try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SignUpView
      name={name}
      email={email}
      password={password}
      confirmPassword={confirmPassword}
      isLoading={isLoading}
      isSaving={isSaving}
      error={error}
      canSignUp={canSignUp}
      onNameChange={onNameChange}
      onEmailChange={onEmailChange}
      onPasswordChange={onPasswordChange}
      onConfirmPasswordChange={onConfirmPasswordChange}
      onPressJoin={handleJoin}
      onSwitchToSignIn={onSwitchToSignIn}
    />
  );
}
